<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { push } from "svelte-spa-router";
  import {
    listDevicesLogs,
    listUsers,
    createDebugSession,
    listDebugSessions,
    stopDebugSession,
    ApiError,
  } from "../lib/api";
  import {
    LOG_LEVELS,
    type LogLevel,
    type LogTargetType,
    type DebugSessionMode,
    type DebugSessionDTO,
    type DeviceLogInfo,
    type CreateDebugSessionRequest,
    type AdminUser,
  } from "@diy-avatar/shared/admin";

  const POLL_MS = 10_000;

  // ---- form state ----
  let targetType: "device" | "student" = "device";
  let deviceLabel = "";
  let userId = "";
  let level: LogLevel = "debug";
  let mode: DebugSessionMode = "window";
  let windowStart = ""; // datetime-local value
  let windowEnd = ""; // datetime-local value
  let wallclockHours = 1;
  let onlineBudgetHours = 1;
  let note = "";

  let creating = false;
  let createError: string | null = null;
  let createOk: string | null = null;

  // ---- option lists ----
  let devices: DeviceLogInfo[] = [];
  let users: AdminUser[] = [];
  let optionsError: string | null = null;

  // ---- active sessions table ----
  let sessions: DebugSessionDTO[] = [];
  let sessionsError: string | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  function handle401(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      push("/");
      return true;
    }
    return false;
  }

  function fmtDate(ms: number | null): string {
    if (ms === null) return "—";
    return new Date(ms).toISOString().slice(0, 19).replace("T", " ");
  }

  /** Local datetime-local string -> epoch ms (null when empty/invalid). */
  function toEpochMs(local: string): number | null {
    if (!local) return null;
    const ms = new Date(local).getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  function fmtRemaining(s: DebugSessionDTO): string {
    if (s.mode === "online_budget") {
      const total = s.budget_seconds_total ?? 0;
      const used = s.budget_seconds_used ?? 0;
      const left = Math.max(0, total - used);
      return `${Math.round(left / 60)}m left`;
    }
    if (s.end_at == null) return "—";
    const left = s.end_at - Date.now();
    if (left <= 0) return "expired";
    const m = Math.round(left / 60000);
    if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
    return `${m}m`;
  }

  function fmtBudget(s: DebugSessionDTO): string {
    if (s.mode === "online_budget" && s.budget_seconds_total != null) {
      const used = Math.round((s.budget_seconds_used ?? 0) / 60);
      const total = Math.round(s.budget_seconds_total / 60);
      return `${used} / ${total} min`;
    }
    return "—";
  }

  function targetLabel(s: DebugSessionDTO): string {
    return `${s.target_type}:${s.target_id}`;
  }

  function userLabel(u: AdminUser): string {
    const name = u.full_name ?? u.display_name ?? u.email;
    const roles = u.roles.map((r) => r.role).join(", ") || "no role";
    return `${name} (${roles})`;
  }

  async function loadOptions() {
    optionsError = null;
    try {
      const res = await listDevicesLogs();
      devices = res.devices;
    } catch (err) {
      if (handle401(err)) return;
      optionsError =
        err instanceof Error ? err.message : "Could not load devices.";
    }
    try {
      const res = await listUsers();
      users = res.users;
    } catch (err) {
      if (handle401(err)) return;
      // Non-fatal: an id input still works without the dropdown.
    }
  }

  async function loadSessions() {
    sessionsError = null;
    try {
      sessions = await listDebugSessions({ active: true });
    } catch (err) {
      if (handle401(err)) return;
      sessionsError =
        err instanceof Error ? err.message : "Could not load sessions.";
    }
  }

  function buildRequest(): CreateDebugSessionRequest | string {
    const target_type: LogTargetType =
      targetType === "device" ? "device" : "student";
    const target_id =
      targetType === "device" ? deviceLabel.trim() : userId.trim();
    if (!target_id) {
      return targetType === "device"
        ? "Pick a device."
        : "Enter a user id.";
    }

    const req: CreateDebugSessionRequest = {
      target_type,
      target_id,
      level,
      mode,
    };

    if (mode === "window") {
      const start = toEpochMs(windowStart);
      const end = toEpochMs(windowEnd);
      if (start == null || end == null) return "Pick a start and end time.";
      if (end <= start) return "End must be after start.";
      req.start_at = start;
      req.end_at = end;
    } else if (mode === "wallclock") {
      if (!(wallclockHours > 0)) return "Enter a positive number of hours.";
      req.budget_hours = wallclockHours;
    } else {
      // online_budget
      if (!(onlineBudgetHours > 0)) return "Enter a positive number of hours.";
      req.budget_hours = onlineBudgetHours;
    }

    const trimmedNote = note.trim();
    if (trimmedNote) req.note = trimmedNote;
    return req;
  }

  async function start() {
    createError = null;
    createOk = null;
    const built = buildRequest();
    if (typeof built === "string") {
      createError = built;
      return;
    }
    creating = true;
    try {
      const session = await createDebugSession(built);
      createOk = `Started debug session #${session.id} for ${targetLabel(session)}.`;
      await loadSessions();
    } catch (err) {
      if (handle401(err)) return;
      createError =
        err instanceof Error ? err.message : "Could not start session.";
    } finally {
      creating = false;
    }
  }

  async function stop(s: DebugSessionDTO) {
    if (
      !window.confirm(
        `Stop debug session #${s.id} for ${targetLabel(s)}?`,
      )
    )
      return;
    try {
      await stopDebugSession(s.id);
      await loadSessions();
    } catch (err) {
      if (handle401(err)) return;
      sessionsError =
        err instanceof Error ? err.message : "Could not stop session.";
    }
  }

  onMount(() => {
    void loadOptions();
    void loadSessions();
    pollTimer = setInterval(() => void loadSessions(), POLL_MS);
  });

  onDestroy(() => {
    if (pollTimer) clearInterval(pollTimer);
  });
</script>

<section>
  <h1>Debug mode</h1>

  <p class="note" data-testid="debug-note">
    Observe a specific device or user at a chosen log level for a fixed
    window, a number of wall-clock hours, or a budget of online-time that is
    only consumed while the target is actually online (e.g. 1h spread over
    future sessions). Sessions stop automatically when their window, time, or
    budget runs out.
  </p>

  <div class="panel" data-testid="debug-create-section">
    <h2>Start a session</h2>
    <div class="row">
      <label>
        Target
        <select bind:value={targetType} data-testid="debug-target-type">
          <option value="device">Device</option>
          <option value="student">User</option>
        </select>
      </label>

      {#if targetType === "device"}
        <label>
          Device
          <select bind:value={deviceLabel} data-testid="debug-device">
            <option value="">— pick a device —</option>
            {#each devices as d (d.label)}
              <option value={d.label}>{d.label}</option>
            {/each}
          </select>
        </label>
      {:else}
        <label>
          User
          <select bind:value={userId} data-testid="debug-student">
            <option value="">— pick a user —</option>
            {#each users as u (u.id)}
              <option value={String(u.id)}>
                {userLabel(u)} (#{u.id})
              </option>
            {/each}
          </select>
        </label>
        <label>
          …or user id
          <input
            type="number"
            min="1"
            bind:value={userId}
            placeholder="user id"
            data-testid="debug-student-id"
          />
        </label>
      {/if}

      <label>
        Level
        <select bind:value={level} data-testid="debug-level">
          {#each LOG_LEVELS as lvl}
            <option value={lvl}>{lvl}</option>
          {/each}
        </select>
      </label>

      <label>
        Mode
        <select bind:value={mode} data-testid="debug-mode">
          <option value="window">Window (start/end)</option>
          <option value="wallclock">Wall-clock hours</option>
          <option value="online_budget">Online-time budget</option>
        </select>
      </label>
    </div>

    <div class="row" data-testid="debug-mode-fields">
      {#if mode === "window"}
        <label>
          Start
          <input
            type="datetime-local"
            bind:value={windowStart}
            data-testid="debug-window-start"
          />
        </label>
        <label>
          End
          <input
            type="datetime-local"
            bind:value={windowEnd}
            data-testid="debug-window-end"
          />
        </label>
      {:else if mode === "wallclock"}
        <label>
          Hours
          <input
            type="number"
            min="0.25"
            step="0.25"
            bind:value={wallclockHours}
            data-testid="debug-wallclock-hours"
          />
        </label>
      {:else}
        <label>
          Hours of online time
          <input
            type="number"
            min="0.25"
            step="0.25"
            bind:value={onlineBudgetHours}
            data-testid="debug-online-hours"
          />
        </label>
        <span class="hint">
          Consumed only while the target is online — e.g. 1h spread over future
          sessions.
        </span>
      {/if}
    </div>

    <div class="row">
      <label class="grow">
        Note (optional)
        <input type="text" bind:value={note} data-testid="debug-note-input" />
      </label>
    </div>

    <div class="row">
      <button on:click={start} disabled={creating} data-testid="debug-start-button">
        {creating ? "Starting…" : "Start session"}
      </button>
    </div>

    {#if optionsError}
      <p class="error" data-testid="debug-options-error">{optionsError}</p>
    {/if}
    {#if createError}
      <p class="error" data-testid="debug-create-error">{createError}</p>
    {/if}
    {#if createOk}
      <p class="ok" data-testid="debug-create-ok">{createOk}</p>
    {/if}
  </div>

  <div class="panel" data-testid="debug-sessions-section">
    <h2>Active sessions</h2>
    {#if sessionsError}
      <p class="error" data-testid="debug-sessions-error">{sessionsError}</p>
    {/if}
    <table data-testid="debug-sessions-table">
      <thead>
        <tr>
          <th>id</th>
          <th>target</th>
          <th>level</th>
          <th>mode</th>
          <th>started</th>
          <th>ends / remaining</th>
          <th>budget used/total</th>
          <th>actions</th>
        </tr>
      </thead>
      <tbody>
        {#each sessions as s (s.id)}
          <tr data-testid="debug-session-row-{s.id}">
            <td>#{s.id}</td>
            <td>{targetLabel(s)}</td>
            <td>{s.level}</td>
            <td>{s.mode}</td>
            <td>{fmtDate(s.start_at ?? s.created_at)}</td>
            <td>
              {#if s.mode === "window" || s.mode === "wallclock"}
                {fmtDate(s.end_at)} · {fmtRemaining(s)}
              {:else}
                {fmtRemaining(s)}
              {/if}
            </td>
            <td>{fmtBudget(s)}</td>
            <td class="actions">
              <button on:click={() => stop(s)} data-testid="debug-stop">
                Stop
              </button>
            </td>
          </tr>
        {/each}
        {#if sessions.length === 0}
          <tr>
            <td colspan="8" class="empty" data-testid="debug-sessions-empty">
              No active sessions.
            </td>
          </tr>
        {/if}
      </tbody>
    </table>
  </div>
</section>

<style>
  h1 {
    margin: 0 0 1rem;
  }
  h2 {
    margin: 0 0 0.5rem;
    font-size: 1rem;
  }
  .note {
    color: #9ca3af;
    font-size: 0.85rem;
    margin: 0 0 1rem;
  }
  .panel {
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
  }
  .row {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    align-items: flex-end;
    margin-bottom: 0.5rem;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.8rem;
    color: #9ca3af;
  }
  label.grow {
    flex: 1;
    min-width: 220px;
  }
  label.grow input {
    width: 100%;
  }
  select,
  input {
    padding: 0.4rem 0.5rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 4px;
  }
  button {
    padding: 0.4rem 0.7rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 4px;
  }
  button:disabled {
    opacity: 0.6;
  }
  .hint {
    align-self: center;
    color: #6b7280;
    font-size: 0.78rem;
    max-width: 280px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    background: #111827;
  }
  th,
  td {
    padding: 0.4rem 0.6rem;
    text-align: left;
    border-bottom: 1px solid #1f2937;
    font-size: 0.85rem;
  }
  th {
    background: #0f172a;
    color: #9ca3af;
  }
  .actions {
    display: flex;
    gap: 0.3rem;
    flex-wrap: wrap;
    align-items: center;
  }
  .empty {
    color: #6b7280;
    text-align: center;
  }
  .error {
    color: #fca5a5;
  }
  .ok {
    color: #6ee7b7;
  }
</style>
